import React, { useState } from 'react';
import style from '../content-type.module.scss';
import { CardChoice as TCardChoice, Autocomplete as TAutocomplete } from '../../types';
import moment from 'moment';
import { EditIcon, DeleteIcon } from '../../../../icons';
import { AddCardChoice } from './AddCardChoice';
import { useMutate, useAppAndBranchId, usePermissions } from '../../../../hooks';
import { api } from '../../api';
import { Modal } from '../../../../components';
import { useToast } from '../../../../../../../../helpers/commonFunction';
import { errorMessage } from '../../../../utils/error';

type Props = {
	cardChoice: (TCardChoice | TAutocomplete) & { id: string; updatedAt: string|null; createdAt: string };
	refetchContent: () => void;
	isAutocomplete?: boolean
};

// CardChoice will be treated as 'Autocomplete' 
// based on the 'isAutocomplete' boolean flag.
// This is done since both these components are very similar with
// minor changes in schema and UI
export function CardChoice(props: Props) {
  const { ErrorToast } = useToast()
	const [appId, branchId] = useAppAndBranchId();
	const { cardChoice, refetchContent, isAutocomplete = false } = props;
	const { mutate: deleteMutate, loading: deleteLoading } = useMutate(
		api.deleteContent,
	);

	const [modalVisibility, setModalVisibility] = useState({
		editContent: false,
		viewContent: false,
		deleteContent: false,
	});
	
	const [canEdit, canDelete] = usePermissions(['content.Edit', 'content.Delete']) as boolean[]

	function updateVisibility(
		key: keyof typeof modalVisibility,
		visibility: boolean,
	) {
		setModalVisibility((prev) => ({
			...prev,
			[key]: visibility,
		}));
	}

	const modalChoices = cardChoice.contentType === 'cardChoice' ? 
		cardChoice.contentValue.choices 
		: cardChoice.contentValue.options.map(el => ({
		id: el.id,
		label: el.label,
	}))

	return (
		<div className={style.contentTypeWrapper}>
			<div data-has-action={canEdit || canDelete} className={style.contentType} onClick={() => {updateVisibility('viewContent', true)}}>
				<div className={style.left}>
					<p>{cardChoice.contentValue.title}</p>
					<p><span>Number of {isAutocomplete ? 'Options' : 'Choices'}:</span> {modalChoices.length}</p>
				</div>
				<div className={style.info}>
					<p>{cardChoice?.updatedAt ? moment(cardChoice?.updatedAt).format('DD MMM YYYY, hh:mm A') : "" }</p>
				</div>
				<div className={style.info}>
					<p>{moment(cardChoice.createdAt).format('DD MMM YYYY, hh:mm A')}</p>
					<div className={style.actions}>
						{(canEdit || canDelete) && <div className={style.line} />}
						{ canEdit &&
							<button
								onClick={(e) => {
									e.stopPropagation()
									updateVisibility('editContent', true);
								}}
							>
								<EditIcon width="14" height="14" stroke="#141B34" />
							</button>
						}{ canDelete &&
							<button
								onClick={async (e) => {
									e.stopPropagation()
									updateVisibility('deleteContent', true);
								}}
							>
								<DeleteIcon width="14" height="14" stroke="#D30000" />
							</button>
						}
					</div>
				</div>
			</div>
			{modalVisibility.viewContent && (
				<AddCardChoice
					isAutocomplete={isAutocomplete}
					mode="view"
					refetchContent={refetchContent}
					initialValue={{
						title: cardChoice.contentValue.title,
						choices: modalChoices as any,
						orientation: cardChoice.contentType === 'cardChoice' ? cardChoice.contentValue.style.orientation : 'H',
						id: cardChoice.id,
						isMulti: cardChoice.contentType === "cardChoice" ? false : cardChoice.contentValue.isMulti,
						isCreatable: cardChoice.contentType === "cardChoice" ? false : cardChoice.contentValue.isCreatable,
						isClearable: cardChoice.contentType === "cardChoice" ? false : cardChoice.contentValue.isClearable,
					}}
					isOpen={modalVisibility.viewContent}
					close={() => {
						updateVisibility('viewContent', false);
					}}
				/>
			)}
			{modalVisibility.editContent && (
				<AddCardChoice
					isAutocomplete={isAutocomplete}
					mode="edit"
					refetchContent={refetchContent}
					initialValue={{
						title: cardChoice.contentValue.title,
						choices: modalChoices as any,
						orientation: cardChoice.contentType === 'cardChoice' ? cardChoice.contentValue.style.orientation : 'H',
						id: cardChoice.id,
						isMulti: cardChoice.contentType === "cardChoice" ? false : cardChoice.contentValue.isMulti,
						isCreatable: cardChoice.contentType === "cardChoice" ? false : cardChoice.contentValue.isCreatable,
						isClearable: cardChoice.contentType === "cardChoice" ? false : cardChoice.contentValue.isClearable,
					}}
					isOpen={modalVisibility.editContent}
					close={() => updateVisibility('editContent', false)}
				/>
			)}
			<Modal
				styles={{
					'--ok-btn-bg': '#DE0000',
					'--ok-btn-filter': 'drop-shadow(0px 8px 13px rgba(211, 0, 73, 0.18))',
				}}
				okText="Delete"
				loading={deleteLoading}
				onOk={async () => {
					try {
						await deleteMutate(appId, branchId, cardChoice.id);
						refetchContent();
					} catch(err) {
					  ErrorToast(errorMessage("delete", isAutocomplete ? "autocomplete" : "card choice", err?.code))
					}
				}}
				onCancel={() => updateVisibility('deleteContent', false)}
				isOpen={modalVisibility.deleteContent}
				title="Remove Chosen Simple Choice"
			>
				<div
					style={{
						padding: '18px 14px',
						color: '#393939',
						fontSize: 15,
						fontWeight: 500,
					}}
				>
					<p>Do you really want to remove the chosen {isAutocomplete ? 'autocomplete' : 'card choice'}?</p>
				</div>
			</Modal>
		</div>
	);
}
